import { withErrorBoundary } from 'app/components/error-boundary';
import { Column } from 'app/design/primitives/box';
import { ListPage } from 'app/design/primitives/page/list';
import { Text } from 'app/design/primitives/text';
import { useMonthlyStreakMembers, type MonthlyStreakMember } from 'app/network/monthly-stats';
import { navigateToUserProfile } from 'app/screens/billboard/navigator-helper';
import MemberCard from 'app/screens/groups/member-card';
import type { NavigationScreen } from 'app/types/navigation';
import { useRegisterLoading } from 'app/utils/performance/screen-perf-context';
import { withOptionalMetrics } from 'app/utils/performance/with-metrics';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ActivityIndicator, Pressable } from 'react-native';

const MemberSeparator = () => <Column marginTop="spacing.general.x4" />;

const StreakMembersScreenContent: NavigationScreen<'streak-members'> = ({ navigation, route }) => {
  const { t } = useTranslation('streak');
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isPending, isRefetching, resetThenRefetch } =
    useMonthlyStreakMembers(route.params.period);

  useEffect(() => {
    navigation.setOptions({ title: t('members') });
  }, [navigation, t]);

  useRegisterLoading(isPending, 'StreakMembersScreen - fetching members');

  const members = useMemo(() => data?.pages.flatMap((page) => page.results) ?? [], [data?.pages]);

  const renderMember = ({ item }: { item: MonthlyStreakMember }) => (
    <Pressable onPress={() => navigateToUserProfile(navigation, item.id)}>
      <MemberCard {...item} hasStreakContribution={item.hasContributed} />
    </Pressable>
  );

  return (
    <ListPage
      data={members}
      ItemSeparatorComponent={MemberSeparator}
      keyExtractor={(item) => item.id}
      ListEmptyComponent={
        isPending ? (
          <ActivityIndicator />
        ) : (
          <Text textAlign="center" variant="bodyBase">
            {t('noMembers')}
          </Text>
        )
      }
      ListFooterComponent={isFetchingNextPage ? <ActivityIndicator /> : null}
      onEndReached={() => {
        if (hasNextPage && !isFetchingNextPage) void fetchNextPage();
      }}
      onEndReachedThreshold={1}
      onRefresh={resetThenRefetch}
      refreshing={isRefetching}
      renderItem={renderMember}
    />
  );
};

export const StreakMembersScreen: NavigationScreen<'streak-members'> = withOptionalMetrics(
  'StreakMembersScreen',
  withErrorBoundary(StreakMembersScreenContent),
);
